import { useState, useMemo } from 'react'
import { Select, Input, Space, Typography, Card, Row, Col, Statistic, Empty, Tag, Pagination } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { EditableTable } from '@/components/EditableTable'
import type { ColumnConfig, ChangeSet } from '@/components/EditableTable/types'
import { useBedFund, useBedFundPeriods, useUpdateBedFund } from '@/hooks/useBedFund'
import { useFilials } from '@/hooks/useRefs'
import type { BedFundViewItem } from '@/types/etl'
import type { Filial } from '@/api/refs'

const { Text } = Typography

const uslOkCodeColors: Record<string, string> = {
  'КС': 'blue',
  'ДСС': 'green',
  'ДСП': 'orange',
  'ДС': 'cyan',
}

const uslOkOptions = [
  { value: '', label: 'Все типы' },
  { value: 'КС', label: 'КС - Круглосуточный стационар' },
  { value: 'ДС', label: 'ДС - Дневной стационар' },
]

export default function BedFundView() {
  const [selectedPeriod, setSelectedPeriod] = useState<string>('')
  const [search, setSearch] = useState('')
  const [filCode, setFilCode] = useState<number | undefined>()
  const [uslOkCode, setUslOkCode] = useState<string>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  // Fetch periods
  const { data: periodsData, isLoading: periodsLoading } = useBedFundPeriods()
  const periods = periodsData?.periods || []

  // Fetch filials
  const { data: filials } = useFilials()
  const filialsList = filials || []

  // Fetch bed fund data
  const { data: bedFundData, isLoading } = useBedFund({
    period: selectedPeriod,
    page,
    page_size: pageSize,
    search: search || undefined,
    fil_code: filCode,
    usl_ok_code: uslOkCode || undefined,
  })

  // Update mutation
  const updateMutation = useUpdateBedFund(selectedPeriod)

  // Auto-select latest period
  useMemo(() => {
    if (periods.length > 0 && !selectedPeriod) {
      setSelectedPeriod(periods[0].period)
    }
  }, [periods, selectedPeriod])

  // Composite rowKey generator
  const getRowKey = (record: BedFundViewItem): string => {
    return `${record.id_otd}-${record.profil_k}`
  }

  // Convert rowKey to API key format
  const rowKeyToApiKey = (rowKey: string): string => {
    // rowKey: "id_otd-profil_k" -> API key: "id_otd|period|profil_k"
    const parts = rowKey.split('-')
    if (parts.length < 2) return rowKey

    const id_otd = parts[0]
    const profil_k = parts.slice(1).join('-') // Handle profil_k with dashes
    return `${id_otd}|${selectedPeriod}|${profil_k}`
  }

  // Columns configuration
  const columns: ColumnConfig[] = useMemo(() => [
    {
      field: 'id_otd',
      title: 'ID',
      type: 'text',
      width: 80,
      editable: false,
    },
    {
      field: 'otd_name',
      title: 'Отделение',
      type: 'text',
      width: 200,
      editable: false,
    },
    {
      field: 'usl_ok_code',
      title: 'Тип',
      type: 'custom',
      width: 70,
      editable: false,
      customRender: (value) => value ? (
        <Tag color={uslOkCodeColors[value] || 'default'}>{value}</Tag>
      ) : '-',
    },
    {
      field: 'profil_k',
      title: 'Профиль койки',
      type: 'text',
      width: 200,
      editable: false,
    },
    {
      field: 'kf_prikaz',
      title: 'По приказу',
      type: 'number',
      width: 120,
      editable: true,
      validation: {
        min: 0,
        message: 'Значение должно быть >= 0',
      },
    },
    {
      field: 'kf_fact',
      title: 'Действующих',
      type: 'number',
      width: 120,
      editable: true,
      validation: {
        min: 0,
        message: 'Значение должно быть >= 0',
      },
    },
    {
      field: 'filial_name',
      title: 'Филиал',
      type: 'text',
      width: 150,
      editable: false,
    },
    {
      field: 'status',
      title: 'Статус отд.',
      type: 'custom',
      width: 100,
      editable: false,
      customRender: (value) => {
        if (!value) return '-'
        const isActive = value === 'Активно'
        return <Tag color={isActive ? 'green' : 'default'}>{value}</Tag>
      },
    },
  ], [])

  // Handle save with validation
  const handleSave = async (changes: ChangeSet) => {
    if (!changes.updated || Object.keys(changes.updated).length === 0) {
      return
    }

    // Validate kf_fact <= kf_prikaz
    const errors: string[] = []
    const validatedUpdates: Record<string, any> = {}

    for (const [rowKey, fields] of Object.entries(changes.updated)) {
      // Find original record
      const record = bedFundData?.items.find(item => getRowKey(item) === rowKey)
      if (!record) continue

      const newKfFact = fields.kf_fact !== undefined ? fields.kf_fact : record.kf_fact
      const newKfPrikaz = fields.kf_prikaz !== undefined ? fields.kf_prikaz : record.kf_prikaz

      // Validation
      if (newKfFact !== null && newKfPrikaz !== null && newKfFact > newKfPrikaz) {
        errors.push(
          `${record.otd_name || record.id_otd} (${record.profil_k}): ` +
          `Действующих коек (${newKfFact}) не может быть больше коек по приказу (${newKfPrikaz})`
        )
        continue
      }

      // Convert rowKey to API key format
      const apiKey = rowKeyToApiKey(rowKey)
      validatedUpdates[apiKey] = {
        kf_fact: fields.kf_fact,
        kf_prikaz: fields.kf_prikaz,
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join('\n'))
    }

    // Send to API
    await updateMutation.mutateAsync(validatedUpdates)
  }


  // Period options
  const periodOptions = periods.map((p) => ({
    value: p.period,
    label: `${p.period_label} (${p.kf_fact} коек)`,
  }))

  // Filial options
  const filialOptions = [
    { value: 0, label: 'Все филиалы' },
    ...filialsList.map((f: Filial) => ({
      value: f.fil_code,
      label: f.fil_name,
    })),
  ]

  if (!selectedPeriod && !periodsLoading) {
    return (
      <Empty
        description="Нет данных по коечному фонду. Загрузите данные на вкладке 'Загрузка КФ'."
      />
    )
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Фильтры */}
      <Space wrap>
        <Select
          style={{ width: 250 }}
          placeholder="Выберите период"
          value={selectedPeriod}
          onChange={(v) => {
            setSelectedPeriod(v)
            setPage(1)
          }}
          options={periodOptions}
          loading={periodsLoading}
        />

        <Input
          style={{ width: 250 }}
          placeholder="Поиск по ID, названию, профилю"
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          allowClear
        />

        <Select
          style={{ width: 200 }}
          placeholder="Тип"
          value={uslOkCode}
          onChange={(v) => {
            setUslOkCode(v)
            setPage(1)
          }}
          options={uslOkOptions}
        />

        <Select
          style={{ width: 200 }}
          placeholder="Филиал"
          value={filCode || 0}
          onChange={(v) => {
            setFilCode(v || undefined)
            setPage(1)
          }}
          options={filialOptions}
          showSearch
          optionFilterProp="label"
        />
      </Space>

      {/* Статистика */}
      {bedFundData && (
        <Row gutter={16}>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="Всего записей"
                value={bedFundData.total}
                valueStyle={{ fontSize: 20 }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="Отделений"
                value={bedFundData.unique_departments}
                valueStyle={{ fontSize: 20, color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="Коек по приказу"
                value={bedFundData.total_kf_prikaz}
                valueStyle={{ fontSize: 20 }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card size="small">
              <Statistic
                title="Коек действующих"
                value={bedFundData.total_kf_fact}
                valueStyle={{ fontSize: 20, color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Text strong>По типам: </Text>
              <Space style={{ marginTop: 8 }}>
                {Object.entries(bedFundData.by_usl_ok).map(([type, stats]) => (
                  <Tag key={type} color={uslOkCodeColors[type] || 'default'}>
                    {type}: {stats.fact} коек
                  </Tag>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {/* Серверная пагинация */}
      {bedFundData && bedFundData.total > 0 && (
        <Card size="small">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={bedFundData.total}
            showSizeChanger
            pageSizeOptions={['20', '50', '100', '200']}
            showTotal={(total, range) => `${range[0]}-${range[1]} из ${total} записей`}
            onChange={(newPage, newPageSize) => {
              setPage(newPage)
              if (newPageSize) setPageSize(newPageSize)
            }}
            style={{ textAlign: 'center' }}
          />
        </Card>
      )}

      {/* Editable Table */}
      <Card>
        <EditableTable
          columns={columns}
          dataSource={(bedFundData?.items || []).map(item => ({...item, _key: getRowKey(item)}))}
          rowKey="_key"
          loading={isLoading || updateMutation.isPending}
          onSave={handleSave}
          draftKey={`bed-fund-${selectedPeriod}-page-${page}`}
          allowAdd={false}
          allowPaste={false}
          allowDelete={false}
        />
      </Card>
    </Space>
  )
}
